import axios, { AxiosInstance } from 'axios';
import { getToken, removeToken } from 'utils/LocalStorageUtils';
import { HttpStatus } from 'config/http-status.config';
import UrlConfig from 'config/url.config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

const backendAxios: AxiosInstance = axios.create({
  baseURL: BACKEND_URL,
  timeout: 10 * 60 * 1000,
});

backendAxios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['Content-Type'] = 'application/json';
  return config;
});

backendAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === HttpStatus.UNAUTHORIZED) {
      removeToken();
      if (!window.location.pathname.includes(UrlConfig.LOGIN)) {
        window.location.href = UrlConfig.LOGIN;
      }
    }
    return Promise.reject(error);
  }
);

interface BackendResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

async function post<T = any>(path: string, body: Record<string, any>): Promise<T> {
  const response = await backendAxios.post<BackendResponse<T>>(path, body);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Backend request failed');
  }
  return response.data.data as T;
}

async function get<T = any>(path: string): Promise<T> {
  const response = await backendAxios.get<BackendResponse<T>>(path);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Backend request failed');
  }
  return response.data.data as T;
}

async function getText(path: string): Promise<string> {
  const response = await backendAxios.get(path, { responseType: 'text' });
  return response.data;
}

async function put<T = any>(path: string, body: Record<string, any>): Promise<T> {
  const response = await backendAxios.put<BackendResponse<T>>(path, body);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Backend request failed');
  }
  return response.data.data as T;
}

async function del<T = any>(path: string): Promise<T> {
  const response = await backendAxios.delete<BackendResponse<T>>(path);
  if (!response.data.success) {
    throw new Error(response.data.error || 'Backend request failed');
  }
  return response.data.data as T;
}

export const backendAxiosInstance = backendAxios;
export const backendClient = { post, get, getText, put, del };
export default backendClient;
